"use client";

import React, { useState, useEffect, useMemo } from "react";
import { ArrowRight, Sparkles, BookOpen, Compass, Cpu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
interface ConsumerProps {
  lang: "en" | "vi";
}
function ProgramGuideConsumer({
  lang
}: ConsumerProps) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  const [selectedAge, setSelectedAge] = useState<string>("all");
  const [selectedGoal, setSelectedGoal] = useState<string>("all");
  const t = {
    en: {
      ageFilters: [{
        id: "all",
        label: "All ages (4–16)"
      }, {
        id: "4-6",
        label: "Ages 4–6 (Early Explorers)"
      }, {
        id: "7-9",
        label: "Ages 7–9 (Foundations)"
      }, {
        id: "10-12",
        label: "Ages 10–12 (Applied Robotics)"
      }, {
        id: "13-16",
        label: "Ages 13–16 (Advanced & AI)"
      }],
      goalFilters: [{
        id: "all",
        label: "All focuses"
      }, {
        id: "tactile",
        label: "Hands-on building & LEGO"
      }, {
        id: "coding",
        label: "Algorithm & software logic"
      }, {
        id: "comp",
        label: "Tournament & team challenge"
      }],
      filterHeadingAge: "Filter by age bracket",
      filterHeadingGoal: "Filter by learning priority",
      emptyHeading: "No specific programs found",
      emptyDesc: "Try adjusting your age or focus filters, or consult our academic counselors for tailored guidance.",
      resetFilters: "Reset filters",
      viewDetails: "View curriculum details",
      trialCta: "Book a trial class",
      exploreCurriculum: "Full learning roadmap",
      ageRangeLabel: "Age group",
      levelLabel: "Level",
      trialNote: "Complimentary 45-minute placement session with teacher evaluation.",
      coreCompetencies: "Core Competencies:",
      loadError: "Unable to load program guide at this moment.",
      tryAgain: "Try again"
    },
    vi: {
      ageFilters: [{
        id: "all",
        label: "Tất cả độ tuổi (4–16)"
      }, {
        id: "4-6",
        label: "4–6 tuổi (Khởi đầu tò mò)"
      }, {
        id: "7-9",
        label: "7–9 tuổi (Nền tảng STEM)"
      }, {
        id: "10-12",
        label: "10–12 tuổi (Robotics ứng dụng)"
      }, {
        id: "13-16",
        label: "13–16 tuổi (Nâng cao & AI)"
      }],
      goalFilters: [{
        id: "all",
        label: "Mọi mục tiêu"
      }, {
        id: "tactile",
        label: "Lắp ráp & Cơ chế LEGO"
      }, {
        id: "coding",
        label: "Tư duy thuật toán & Code"
      }, {
        id: "comp",
        label: "Thi đấu & Dự án sáng tạo"
      }],
      filterHeadingAge: "Chọn theo độ tuổi của bé",
      filterHeadingGoal: "Chọn theo mục tiêu phát triển",
      emptyHeading: "Chưa có chương trình phù hợp",
      emptyDesc: "Vui lòng chọn bộ lọc khác hoặc liên hệ thầy cô ROBOTO để nhận tư vấn lộ trình riêng.",
      resetFilters: "Đặt lại bộ lọc",
      viewDetails: "Chi tiết giáo trình",
      trialCta: "Đăng ký học thử",
      exploreCurriculum: "Lộ trình đào tạo chuẩn",
      ageRangeLabel: "Độ tuổi",
      levelLabel: "Trình độ",
      trialNote: "Buổi trải nghiệm 45 phút miễn phí kèm đánh giá năng lực từ giáo viên.",
      coreCompetencies: "Năng lực cốt lõi:",
      loadError: "Hiện chưa thể tải hướng dẫn chương trình lúc này.",
      tryAgain: "Thử lại"
    }
  }[lang];
  const filteredPosts = useMemo(() => {
    if (!posts || posts.length === 0) return [];
    return posts.filter(post => {
      const ageStr = (post.customFields?.age_range || "").toLowerCase();
      const titleStr = (post.customFields?.program_name || post.title || "").toLowerCase();
      const taglineStr = (post.customFields?.tagline || "").toLowerCase();
      const skillsStr = (post.customFields?.skills || "").toLowerCase();
      const combinedMeta = `${titleStr} ${taglineStr} ${skillsStr} ${ageStr}`;

      // Age bracket filter
      if (selectedAge !== "all") {
        if (selectedAge === "4-6" && !ageStr.includes("4") && !ageStr.includes("5") && !ageStr.includes("6")) {
          return false;
        }
        if (selectedAge === "7-9" && !ageStr.includes("7") && !ageStr.includes("8") && !ageStr.includes("9")) {
          return false;
        }
        if (selectedAge === "10-12" && !ageStr.includes("10") && !ageStr.includes("11") && !ageStr.includes("12")) {
          return false;
        }
        if (selectedAge === "13-16" && !ageStr.includes("13") && !ageStr.includes("14") && !ageStr.includes("15") && !ageStr.includes("16")) {
          return false;
        }
      }

      // Goal filter
      if (selectedGoal !== "all") {
        if (selectedGoal === "tactile" && !combinedMeta.includes("lego") && !combinedMeta.includes("build") && !combinedMeta.includes("lắp ráp") && !combinedMeta.includes("cơ chế")) {
          return false;
        }
        if (selectedGoal === "coding" && !combinedMeta.includes("cod") && !combinedMeta.includes("python") && !combinedMeta.includes("scratch") && !combinedMeta.includes("thuật toán")) {
          return false;
        }
        if (selectedGoal === "comp" && !combinedMeta.includes("comp") && !combinedMeta.includes("thi") && !combinedMeta.includes("wro") && !combinedMeta.includes("fll") && !combinedMeta.includes("dự án")) {
          return false;
        }
      }
      return true;
    });
  }, [posts, selectedAge, selectedGoal]);
  if (loading) {
    return <div className="space-y-8">
        <div className="flex flex-wrap gap-2 justify-center">
          {Array.from({
          length: 5
        }).map((_, i) => <Skeleton key={i} className="h-9 w-32 rounded-full" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({
          length: 3
        }).map((_, i) => <Skeleton key={i} className="h-72 w-full rounded-xl" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 text-center text-foreground">
        <p className="text-sm font-medium">{t.loadError}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
          {t.tryAgain}
        </Button>
      </div>;
  }
  return <div className="space-y-8">
      {/* Interactive Quick Navigation Chips */}
      <div className="space-y-4 rounded-xl border border-border bg-card p-4 sm:p-6 shadow-xs">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-2.5">
            {t.filterHeadingAge}
          </span>
          <div className="flex flex-wrap gap-2">
            {t.ageFilters.map((age, i) => {
            const active = selectedAge === age.id;
            return <Button key={age.id} data-index={i} type="button" onClick={() => setSelectedAge(age.id)} className={`h-auto inline-flex items-center rounded-full px-3.5 py-1.5 text-xs font-medium transition-[background-color,color,border-color] ${active ? "bg-primary text-primary-foreground shadow-xs" : "bg-muted text-foreground hover:bg-muted/80 border border-transparent"}`}>
                  {age.label}
                </Button>;
          })}
          </div>
        </div>

        <div className="pt-2 border-t border-border">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-2.5">
            {t.filterHeadingGoal}
          </span>
          <div className="flex flex-wrap gap-2">
            {t.goalFilters.map((goal, i) => {
            const active = selectedGoal === goal.id;
            return <Button key={goal.id} data-index={i} type="button" onClick={() => setSelectedGoal(goal.id)} className={`h-auto inline-flex items-center rounded-full px-3.5 py-1.5 text-xs font-medium transition-[background-color,color,border-color] ${active ? "bg-secondary text-secondary-foreground shadow-xs" : "bg-muted text-foreground hover:bg-muted/80 border border-transparent"}`}>
                  {goal.label}
                </Button>;
          })}
          </div>
        </div>
      </div>

      {/* Program Cards Grid */}
      {filteredPosts.length === 0 ? <div className="rounded-xl border border-dashed border-border p-12 text-center bg-card">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground mb-4">
            <Compass className="size-6" />
          </div>
          <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
            {t.emptyHeading}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
            {t.emptyDesc}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button variant="outline" size="sm" onClick={() => {
          setSelectedAge("all");
          setSelectedGoal("all");
        }}>
              {t.resetFilters}
            </Button>
            <Button asChild size="sm">
              <Link to="/FreeTrialContactPage">{t.trialCta}</Link>
            </Button>
          </div>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post, i) => {
        const programName = post.customFields?.program_name || post.title || "ROBOTO Track";
        const tagline = post.customFields?.tagline || post.excerpt || "Hands-on robotics journey designed for progressive engineering mastery.";
        const ageRange = post.customFields?.age_range || "4–16";
        const level = post.customFields?.level || "Beginner – Advanced";
        return <Card key={post.id} data-index={i} className="flex flex-col justify-between border-border hover:border-secondary/60 transition-[border-color,box-shadow] shadow-xs hover:shadow-md bg-card">
                <CardHeader className="space-y-2 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="border-secondary/40 text-foreground bg-secondary/10">
                      {t.ageRangeLabel}: {ageRange}
                    </Badge>
                    <span className="text-[11px] font-mono text-muted-foreground uppercase">
                      {level}
                    </span>
                  </div>
                  <CardTitle className="font-serif text-xl text-foreground font-bold tracking-tight pt-1">
                    {programName}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm line-clamp-3 min-w-0">
                    {tagline}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3 pt-2 min-w-0">
                  {post.customFields?.skills && <div className="rounded-md bg-muted/60 p-2.5 text-xs text-foreground space-y-1 min-w-0">
                      <span className="font-medium text-muted-foreground block text-[11px] uppercase tracking-wide">
                        {t.coreCompetencies}
                      </span>
                      <p className="line-clamp-2">{post.customFields.skills}</p>
                    </div>}
                </CardContent>

                <CardFooter className="flex flex-col gap-2.5 pt-4 border-t border-border mt-auto">
                  <div className="grid grid-cols-2 gap-2 w-full">
                    {post.link ? <Button asChild variant="outline" size="sm" className="w-full text-xs">
                        <Link to={post.link}>
                          <span>{t.viewDetails}</span>
                        </Link>
                      </Button> : <Button asChild variant="outline" size="sm" className="w-full text-xs">
                        <Link to="/LearningPathCurriculumPage">
                          <span>{t.exploreCurriculum}</span>
                        </Link>
                      </Button>}
                    <Button asChild size="sm" className="w-full text-xs">
                      <Link to="/FreeTrialContactPage">
                        <span>{t.trialCta}</span>
                      </Link>
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground text-center">
                    {t.trialNote}
                  </p>
                </CardFooter>
              </Card>;
      })}
        </div>}

      {/* Global Section Footnote & Routing Hub */}
      <div className="mt-12 rounded-xl bg-muted/50 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="space-y-1 text-center sm:text-left">
          <h4 className="font-serif text-lg font-bold text-foreground">
            {lang === "vi" ? "Chưa chắc chắn về xuất phát điểm của con?" : "Unsure where your child fits best?"}
          </h4>
          <p className="text-sm text-muted-foreground">
            {lang === "vi" ? "Xem toàn bộ khung chương trình chuẩn quốc tế hoặc đặt lịch làm bài kiểm tra năng lực trực tiếp." : "Review the unified K–12 robotics framework or arrange a placement diagnostic with an instructor."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <Button asChild variant="outline">
            <Link to="/LearningPathCurriculumPage">
              <BookOpen className="size-4" />
              <span>{lang === "vi" ? "Xem toàn bộ lộ trình" : "Full Curriculum Map"}</span>
            </Link>
          </Button>
          <Button asChild>
            <Link to="/FreeTrialContactPage">
              <span>{lang === "vi" ? "Đặt lịch đánh giá miễn phí" : "Schedule Free Evaluation"}</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function ProgramGuide() {
  const [lang, setLang] = useState<"en" | "vi">("vi");
  useEffect(() => {
    const saved = localStorage.getItem("roboto_lang");
    if (saved === "vi" || saved === "en") {
      setLang(saved);
    }
    const handleLangChange = (e: CustomEvent<{
      lang: "en" | "vi";
    }>) => {
      if (e.detail?.lang) {
        setLang(e.detail.lang);
      } else {
        const current = localStorage.getItem("roboto_lang");
        if (current === "vi" || current === "en") {
          setLang(current);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange as EventListener);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange as EventListener);
    };
  }, []);
  return <section id="parentprogramguide" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 relative overflow-hidden">
      {/* Decorative Static Floating Mascot */}
      <div aria-hidden="true" className="pointer-events-none select-none absolute top-4 right-4 sm:top-6 sm:right-6 lg:top-8 lg:right-10 z-0 hidden sm:block">
        <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt="" className="h-24 w-24 sm:h-28 sm:w-28 lg:h-32 lg:w-32 object-contain bg-transparent drop-shadow-md opacity-85 transition-opacity" loading="lazy" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Engineered 1px lime-green spec rail and header */}
        <div className="relative mb-12 sm:mb-16">
          <div aria-hidden="true" className="absolute -top-6 left-0 h-0.5 w-24 bg-secondary" />

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="space-y-3 max-w-2xl lg:max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-md bg-secondary/15 px-2.5 py-1 text-xs font-semibold text-secondary-foreground border border-secondary/30">
                <Sparkles className="size-3.5 text-secondary" />
                <span>
                  {lang === "vi" ? "HƯỚNG DẪN DÀNH CHO PHỤ HUYNH" : "PARENT DECISION GUIDE"}
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-tight">
                {lang === "vi" ? "Chọn đúng chương trình Robotics cho con bạn." : "Finding the right robotics track for your child."}
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                {lang === "vi" ? "Giải đáp thẳng thắn độ tuổi bắt đầu, định hướng phát triển tư duy thuật toán, và cách đo lường tiến bộ thực chất của học sinh." : "Explore age-appropriate learning journeys from early tactile construction to competitive engineering, verified through milestone projects."}
              </p>
            </div>

            {/* Language indicator / quick badge */}
            <div className="hidden sm:flex items-center gap-1.5 self-start sm:self-center text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full shrink-0">
              <Cpu className="size-3.5 text-primary" />
              <span>
                {lang === "vi" ? "Khung Chương Trình ROBOTO K–12" : "ROBOTO K–12 Academy Framework"}
              </span>
            </div>
          </div>
        </div>

        {/* Dynamic WordPress CPT Consumer via PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "program",
        per_page: 20,
        status: ["publish"]
      }}>
          <ProgramGuideConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}